import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { I, Toggle } from "./primitives";

// Format a byte count as KB / MB / GB. Used for the DB size label in the
// Pembersihan Data section.
function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Settings page backed by the hub's app_settings row. Edits are buffered in
 * local form state and saved with a short debounce — the dashboard's threshold
 * pips and the alerting cron pick up the new values on their next read of
 * the settings row.
 */
export function SettingsPage({ settings, onSave, lastSavedAt }) {
  const [form, setForm] = useState(settings);
  const [showToken, setShowToken] = useState(false);
  const [tokenDraft, setTokenDraft] = useState("");
  const [testStatus, setTestStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  // ── Security: sessions ──
  const [sessionsList, setSessionsList] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // ── Security: password ──
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwStatus, setPwStatus] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);
  // ── Backup & Restore ──
  const [sysInfo, setSysInfo] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreError, setRestoreError] = useState(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreFileInputRef = useRef(null);

  // ── Pembersihan Data (wipe transactional tables) ──
  const [maintStats, setMaintStats] = useState(null);
  const [clearTarget, setClearTarget] = useState(null); // "notifications" | "metrics" | null
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);
  const [clearStatus, setClearStatus] = useState(null);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const r = await api.listSessions();
      setSessionsList(r.sessions || []);
    } catch { /* ignore if auth disabled */ }
    finally { setSessionsLoading(false); }
  }, []);

  // Load sessions once on mount
  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Load system info (db path label) once on mount.
  useEffect(() => {
    let cancelled = false;
    api.systemInfo()
      .then((info) => { if (!cancelled) setSysInfo(info); })
      .catch(() => { /* ignore — label just shows '—' */ });
    return () => { cancelled = true; };
  }, []);

  // Maintenance stats — row counts + DB size. Refresh after a clear so the
  // counts drop to zero and the operator sees confirmation.
  const fetchMaintStats = useCallback(async () => {
    try {
      const r = await api.maintenanceStats();
      setMaintStats(r);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchMaintStats(); }, [fetchMaintStats]);

  const openClearDialog = (target) => {
    setClearTarget(target);
    setClearConfirmText("");
    setClearStatus(null);
  };

  const handleConfirmClear = async () => {
    if (clearConfirmText !== "HAPUS" || !clearTarget) return;
    setClearing(true);
    setClearStatus(null);
    try {
      const r = clearTarget === "notifications"
        ? await api.clearNotifications("HAPUS")
        : await api.clearMetricsHistory("HAPUS");
      setClearStatus({ ok: true, target: clearTarget, deleted: r.deleted });
      await fetchMaintStats();
      setClearTarget(null);
      setClearConfirmText("");
    } catch (e) {
      setClearStatus({ ok: false, target: clearTarget, msg: e.body?.error || e.message || "Gagal" });
    } finally {
      setClearing(false);
    }
  };

  const handleDownloadBackup = async () => {
    setBackupBusy(true);
    setBackupStatus(null);
    try {
      const r = await api.downloadBackup();
      const sizeKB = (r.size / 1024).toFixed(1);
      setBackupStatus({ ok: true, msg: `Backup tersimpan: ${r.filename} (${sizeKB} KB)` });
    } catch (e) {
      setBackupStatus({ ok: false, msg: e.body?.error || e.message || "Gagal download backup" });
    } finally {
      setBackupBusy(false);
      setTimeout(() => setBackupStatus(null), 8000);
    }
  };

  const handleSelectRestoreFile = (e) => {
    setRestoreError(null);
    setRestorePreview(null);
    setRestoreResult(null);
    const f = e.target.files?.[0];
    if (!f) {
      setRestoreFile(null);
      return;
    }
    setRestoreFile(f);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(String(reader.result));
        if (typeof json !== "object" || json === null) throw new Error("File bukan JSON object");
        if (!Array.isArray(json.servers) || !Array.isArray(json.agentTokens)) {
          throw new Error("Struktur backup tidak valid (servers / agentTokens hilang)");
        }
        setRestorePreview({
          version: json.version,
          exportedAt: json.exportedAt,
          dbPath: json.dbPath,
          servers: json.servers.length,
          agentTokens: json.agentTokens.length,
          appSettings: !!json.appSettings,
          raw: json,
        });
      } catch (err) {
        setRestoreError(err.message || "File tidak bisa di-parse");
      }
    };
    reader.onerror = () => setRestoreError("Gagal membaca file");
    reader.readAsText(f);
  };

  const handleRestoreConfirmed = async () => {
    if (!restorePreview?.raw) return;
    setRestoring(true);
    setRestoreError(null);
    setRestoreResult(null);
    try {
      const r = await api.restoreBackup(restorePreview.raw);
      setRestoreResult(r);
      setRestoreConfirmOpen(false);
      setRestoreFile(null);
      setRestorePreview(null);
      if (restoreFileInputRef.current) restoreFileInputRef.current.value = "";
    } catch (e) {
      setRestoreError(e.body?.message || e.body?.error || e.message || "Restore gagal");
    } finally {
      setRestoring(false);
    }
  };

  // Resync local form whenever the upstream settings object changes (e.g.
  // first load, or a save round-trip). Don't blow away an in-flight `tokenDraft`.
  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const queueSave = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      setSaveError(null);
      try {
        await onSave(patch);
      } catch (e) {
        setSaveError(e.message || String(e));
      } finally {
        setSaving(false);
      }
    }, 600);
  };

  const handleSaveToken = async () => {
    if (!tokenDraft.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ telegramToken: tokenDraft.trim() });
      setTokenDraft("");
    } catch (e) {
      setSaveError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleClearToken = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ telegramToken: null });
      setTokenDraft("");
    } catch (e) {
      setSaveError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    setTestStatus(null);
    try {
      const r = await api.testNotification();
      setTestStatus({
        ok: !!r.delivered,
        msg: r.delivered ? `Terkirim ke ${form.telegramChatId}` : `Gagal: ${r.error}`,
      });
    } catch (e) {
      const errMsg = e.body?.error || e.message || String(e);
      setTestStatus({ ok: false, msg: `Gagal: ${errMsg}` });
    } finally {
      setTesting(false);
      setTimeout(() => setTestStatus(null), 7000);
    }
  };

  const savedAgo = useMemo(() => {
    if (!lastSavedAt) return null;
    const sec = Math.floor((Date.now() - lastSavedAt) / 1000);
    if (sec < 5) return "Tersimpan baru saja";
    if (sec < 60) return `Tersimpan ${sec}s yang lalu`;
    return `Tersimpan ${Math.floor(sec / 60)}m yang lalu`;
  }, [lastSavedAt]);

  return (
    <div className="page">
      <div className="page-narrow">
        <h1 className="page-title">Pengaturan Notifikasi</h1>
        <p className="page-sub">
          Atur bot Telegram dan ambang batas (threshold) untuk peringatan otomatis. Perubahan
          disimpan otomatis ke hub.
        </p>

        {/* save status pill */}
        <div
          className="flex items-center gap-2"
          style={{
            marginBottom: 16,
            fontSize: 12,
            color: saveError ? "var(--crit)" : "var(--text-3)",
            minHeight: 18,
          }}
        >
          {saving && <span>Menyimpan…</span>}
          {!saving && saveError && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <I.alert /> {saveError}
            </span>
          )}
          {!saving && !saveError && savedAgo && <span>{savedAgo}</span>}
        </div>

        {/* Telegram Bot */}
        <div className="card-section">
          <h3>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, verticalAlign: "middle" }}>
              <span style={{ color: "var(--accent)" }}><I.telegram /></span>
              Telegram Bot
            </span>
          </h3>
          <p>Satu bot menangani notifikasi untuk semua server. Dapatkan token dari @BotFather.</p>

          <div className="field">
            <label className="field-label">
              Bot Token
              {form.telegramTokenSet && (
                <span className="region-status" style={{ color: "var(--ok)" }}>
                  <I.check /> <span>tersimpan</span>
                </span>
              )}
            </label>
            <div className="input">
              <input
                type={showToken ? "text" : "password"}
                value={tokenDraft}
                onChange={(e) => setTokenDraft(e.target.value)}
                placeholder={form.telegramTokenSet ? `${form.telegramToken || "•••••••"} (set new to replace)` : "123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"}
                style={{ fontFamily: "var(--font-mono)" }}
              />
              <button
                className="btn-icon bare"
                style={{ width: 24, height: 24 }}
                title={showToken ? "Sembunyikan" : "Tampilkan"}
                onClick={() => setShowToken(!showToken)}
                type="button"
              >
                {showToken ? <I.close /> : <I.copy />}
              </button>
            </div>
            <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
              <button
                type="button"
                className="btn"
                disabled={!tokenDraft.trim() || saving}
                onClick={handleSaveToken}
                style={{ opacity: !tokenDraft.trim() || saving ? 0.55 : 1 }}
              >
                <span>Simpan Token</span>
              </button>
              {form.telegramTokenSet && (
                <button
                  type="button"
                  className="btn btn-ghost danger"
                  onClick={handleClearToken}
                  disabled={saving}
                >
                  <span>Hapus Token</span>
                </button>
              )}
              <div className="field-hint" style={{ marginTop: 0 }}>
                Token disimpan di DB hub. Setelah disimpan, hanya prefix yang ditampilkan.
              </div>
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">Chat ID</label>
              <div className="input">
                <input
                  value={form.telegramChatId ?? ""}
                  onChange={(e) => queueSave({ telegramChatId: e.target.value })}
                  placeholder="-1001234567890"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>
              <div className="field-hint">Group / channel ID tujuan</div>
            </div>
            <div className="field">
              <label className="field-label">Format Pesan</label>
              <div className="input" style={{ position: "relative" }}>
                <select
                  value={form.telegramFormat}
                  onChange={(e) => queueSave({ telegramFormat: e.target.value })}
                  style={{
                    appearance: "none",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: 13,
                    padding: 0,
                  }}
                >
                  <option value="plain">Plain Text</option>
                  <option value="markdown">MarkdownV2</option>
                  <option value="html">HTML</option>
                </select>
                <I.chevronDown />
              </div>
              <div className="field-hint">Style pesan ke Telegram</div>
            </div>
          </div>

          <div className="flex items-center gap-3" style={{ marginTop: 8 }}>
            <button
              className="btn"
              onClick={handleTestNotification}
              disabled={testing || !form.telegramTokenSet || !form.telegramChatId}
              style={{
                opacity: testing || !form.telegramTokenSet || !form.telegramChatId ? 0.55 : 1,
              }}
              type="button"
            >
              <I.send />
              <span>{testing ? "Mengirim…" : "Kirim Test Notifikasi"}</span>
            </button>
            {testStatus && (
              <span
                className="text-sm mono"
                style={{
                  color: testStatus.ok ? "var(--ok)" : "var(--crit)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {testStatus.ok ? <I.check /> : <I.alert />}
                {testStatus.msg}
              </span>
            )}
          </div>

          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 8,
              background: "var(--bg)",
              border: "1px solid var(--border-soft)",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--text-2)",
              lineHeight: 1.65,
            }}
          >
            <div style={{ color: "var(--text-3)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
              Preview Pesan
            </div>
            <div>⚠️ <b>Server web-04</b>: CPU <b>92%</b> melebihi batas ({form.thresholdCpu}%)</div>
            <div style={{ color: "var(--text-3)" }}>2026-05-20 14:32:18 · jkt-1 · 10.0.4.21</div>
          </div>
        </div>

        {/* Thresholds */}
        <div className="card-section">
          <h3>Ambang Batas (Global)</h3>
          <p>Threshold per metrik. Server yang melewatinya akan men-trigger notifikasi.</p>

          <ThresholdRow
            icon={<I.cpu />}
            iconBg="oklch(0.78 0.12 195 / 0.18)"
            iconColor="var(--accent)"
            label="CPU"
            value={form.thresholdCpu}
            onChange={(v) => queueSave({ thresholdCpu: v })}
          />
          <ThresholdRow
            icon={<I.ram />}
            iconBg="oklch(0.7 0.14 280 / 0.18)"
            iconColor="oklch(0.78 0.12 280)"
            label="RAM"
            value={form.thresholdRam}
            onChange={(v) => queueSave({ thresholdRam: v })}
          />
          <ThresholdRow
            icon={<I.disk />}
            iconBg="oklch(0.78 0.14 80 / 0.18)"
            iconColor="oklch(0.83 0.14 80)"
            label="Disk"
            value={form.thresholdDisk}
            onChange={(v) => queueSave({ thresholdDisk: v })}
          />
        </div>

        {/* Notification rules */}
        <div className="card-section">
          <h3>Aturan Notifikasi</h3>
          <p>Kontrol kapan notifikasi dikirim.</p>

          <RuleRow
            label="Kirim notifikasi saat threshold terlampaui"
            sub="Pemicu utama: status server berubah menjadi 'over limit'"
            value={form.notifyOnCrit}
            onChange={(v) => queueSave({ notifyOnCrit: v })}
          />
          <RuleRow
            label="Kirim notifikasi saat status kembali normal"
            sub="Pesan recovery: '✅ web-01 CPU kembali normal (45%)'"
            value={form.notifyOnRecover}
            onChange={(v) => queueSave({ notifyOnRecover: v })}
          />
          <RuleRow
            label="Kirim notifikasi saat server offline"
            sub={`Saat agent tidak merespons > ${Math.round(form.offlineThresholdMs / 1000)} detik`}
            value={form.notifyOnOffline}
            onChange={(v) => queueSave({ notifyOnOffline: v })}
          />
          <RuleRow
            label="Cooldown notifikasi"
            sub={`Tunggu ${form.cooldownSeconds}s sebelum re-alert untuk metrik yang masih crit`}
            value={form.cooldownEnabled}
            onChange={(v) => queueSave({ cooldownEnabled: v })}
          />
          <NumberRow
            label="Sustain duration (filter lonjakan sementara)"
            sub={
              form.sustainSeconds > 0
                ? `Tunggu ${form.sustainSeconds}s breach bertahan sebelum kirim notifikasi — lonjakan singkat (<${form.sustainSeconds}s) tidak akan men-trigger.`
                : "Disabled — notifikasi kirim langsung saat threshold terlampaui (legacy)."
            }
            value={form.sustainSeconds ?? 60}
            min={0}
            max={3600}
            step={5}
            unit="detik"
            onChange={(v) => queueSave({ sustainSeconds: v })}
            last
          />
        </div>

        {/* Security: Active Sessions */}
        <div className="card-section">
          <h3>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              🔐 Keamanan — Sesi Aktif
            </span>
          </h3>
          <p>Daftar semua sesi login yang aktif. Revoke sesi yang tidak dikenal.</p>

          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-soft)", textAlign: "left" }}>
                  <th style={{ padding: "8px 6px", color: "var(--text-3)", fontWeight: 500 }}>ID</th>
                  <th style={{ padding: "8px 6px", color: "var(--text-3)", fontWeight: 500 }}>IP</th>
                  <th style={{ padding: "8px 6px", color: "var(--text-3)", fontWeight: 500 }}>Browser</th>
                  <th style={{ padding: "8px 6px", color: "var(--text-3)", fontWeight: 500 }}>Last Used</th>
                  <th style={{ padding: "8px 6px" }}></th>
                </tr>
              </thead>
              <tbody>
                {sessionsList.map((s) => (
                  <tr key={s.fullId} style={{ borderBottom: "1px solid var(--border-soft)" }}>
                    <td style={{ padding: "8px 6px", fontFamily: "var(--font-mono)" }}>
                      {s.id}
                      {s.current && (
                        <span style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 600,
                          color: "var(--ok, #34c759)",
                          background: "rgba(52,199,89,0.1)",
                          padding: "1px 6px", borderRadius: 4,
                        }}>current</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 6px", fontFamily: "var(--font-mono)" }}>{s.ip}</td>
                    <td style={{ padding: "8px 6px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.userAgent.length > 40 ? s.userAgent.slice(0, 40) + "…" : s.userAgent}
                    </td>
                    <td style={{ padding: "8px 6px", color: "var(--text-3)" }}>
                      {new Date(s.lastUsedAt).toLocaleString()}
                    </td>
                    <td style={{ padding: "8px 6px" }}>
                      {!s.current && (
                        <button
                          className="btn btn-ghost danger"
                          style={{ fontSize: 11, padding: "3px 8px" }}
                          onClick={async () => {
                            await api.revokeSession(s.fullId);
                            fetchSessions();
                          }}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {sessionsList.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: "16px 6px", textAlign: "center", color: "var(--text-3)" }}>
                      {sessionsLoading ? "Memuat sesi…" : "Tidak ada sesi aktif"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
            <button
              className="btn btn-ghost"
              onClick={fetchSessions}
              disabled={sessionsLoading}
              type="button"
              style={{ fontSize: 12 }}
            >
              <I.refresh /> <span>Refresh</span>
            </button>
            {sessionsList.length > 1 && (
              <button
                className="btn btn-ghost danger"
                onClick={async () => {
                  await api.revokeAllSessions();
                  fetchSessions();
                }}
                type="button"
                style={{ fontSize: 12 }}
              >
                Revoke Semua Sesi Lain
              </button>
            )}
          </div>
        </div>

        {/* Security: Change Password */}
        <div className="card-section">
          <h3>🔑 Ganti Password</h3>
          <p>Ganti password dashboard. Password baru disimpan ter-enkripsi di DB dan tetap aktif setelah hub di-restart.</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 360, marginTop: 12 }}>
            <div className="field">
              <label className="field-label">Password Saat Ini</label>
              <div className="input">
                <input
                  type="password"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div className="field">
              <label className="field-label">Password Baru (min 8 karakter)</label>
              <div className="input">
                <input
                  type="password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="field">
              <label className="field-label">Konfirmasi Password Baru</label>
              <div className="input">
                <input
                  type="password"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                className="btn"
                type="button"
                disabled={pwSaving || !pwCurrent || !pwNew || pwNew.length < 8 || pwNew !== pwConfirm}
                style={{
                  opacity: pwSaving || !pwCurrent || !pwNew || pwNew.length < 8 || pwNew !== pwConfirm ? 0.55 : 1,
                }}
                onClick={async () => {
                  setPwSaving(true);
                  setPwStatus(null);
                  try {
                    const r = await api.changePassword(pwCurrent, pwNew);
                    setPwStatus({ ok: true, msg: `Password berhasil diubah. ${r.revoked_sessions || 0} sesi lain di-revoke.` });
                    setPwCurrent("");
                    setPwNew("");
                    setPwConfirm("");
                    fetchSessions();
                  } catch (e) {
                    const errMsg = e.body?.error || e.message || String(e);
                    setPwStatus({ ok: false, msg: errMsg === "invalid_current_password" ? "Password saat ini salah" : `Error: ${errMsg}` });
                  } finally {
                    setPwSaving(false);
                  }
                }}
              >
                {pwSaving ? "Menyimpan…" : "Ganti Password"}
              </button>
              {pwNew && pwConfirm && pwNew !== pwConfirm && (
                <span className="text-xs" style={{ color: "var(--crit)" }}>Password tidak cocok</span>
              )}
            </div>

            {pwStatus && (
              <div
                style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: 12,
                  background: pwStatus.ok ? "rgba(52,199,89,0.08)" : "rgba(255,69,58,0.08)",
                  border: `1px solid ${pwStatus.ok ? "rgba(52,199,89,0.3)" : "rgba(255,69,58,0.3)"}`,
                  color: pwStatus.ok ? "var(--ok, #34c759)" : "var(--crit, #ff453a)",
                }}
              >
                {pwStatus.ok ? "✓" : "✕"} {pwStatus.msg}
              </div>
            )}
          </div>
        </div>

        {/* Backup & Restore */}
        <div className="card-section">
          <h3>💾 Backup & Restore</h3>
          <p>
            Snapshot data server, token agent, dan konfigurasi. <b>Tidak termasuk</b> riwayat
            metrik, notifikasi, dan sesi (supaya file backup tetap ringan).
          </p>

          <div className="db-path-label">
            <span className="db-path-label-key">DATABASE</span>
            <span className="db-path-label-val mono">{sysInfo?.dbPath ?? "—"}</span>
          </div>

          <div className="backup-grid">
            <div className="backup-card">
              <div className="backup-card-head">
                <span style={{ color: "var(--accent)" }}><I.copy /></span>
                <span className="backup-card-title">Download Backup</span>
              </div>
              <div className="backup-card-body">
                File JSON berisi <span className="mono">servers</span> +{" "}
                <span className="mono">agent_tokens</span> +{" "}
                <span className="mono">app_settings</span>. Aman disimpan off-site.
              </div>
              <button
                className="btn"
                type="button"
                onClick={handleDownloadBackup}
                disabled={backupBusy}
                style={{ opacity: backupBusy ? 0.55 : 1 }}
              >
                <span>{backupBusy ? "Menyiapkan…" : "Download Backup"}</span>
              </button>
              {backupStatus && (
                <div
                  style={{
                    marginTop: 10, fontSize: 12,
                    color: backupStatus.ok ? "var(--ok)" : "var(--crit)",
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  {backupStatus.ok ? <I.check /> : <I.alert />}
                  <span>{backupStatus.msg}</span>
                </div>
              )}
            </div>

            <div className="backup-card">
              <div className="backup-card-head">
                <span style={{ color: "var(--warn)" }}><I.alert /></span>
                <span className="backup-card-title">Restore dari File</span>
              </div>
              <div className="backup-card-body">
                ⚠ Replace seluruh <span className="mono">servers</span>,{" "}
                <span className="mono">agent_tokens</span>, dan{" "}
                <span className="mono">app_settings</span> dari file backup.
                Riwayat metrik <b>tidak terpengaruh</b>.
              </div>
              <input
                ref={restoreFileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={handleSelectRestoreFile}
                style={{ fontSize: 12, marginBottom: 8, color: "var(--text-2)" }}
              />
              {restoreError && (
                <div className="backup-error">
                  <I.alert /> <span>{restoreError}</span>
                </div>
              )}
              {restorePreview && !restoreError && (
                <div className="backup-preview">
                  <div className="backup-preview-row">
                    <span className="backup-preview-key">Diekspor</span>
                    <span className="mono">
                      {restorePreview.exportedAt
                        ? new Date(restorePreview.exportedAt).toLocaleString()
                        : "—"}
                    </span>
                  </div>
                  <div className="backup-preview-row">
                    <span className="backup-preview-key">Source DB</span>
                    <span className="mono" style={{ wordBreak: "break-all" }}>
                      {restorePreview.dbPath ?? "—"}
                    </span>
                  </div>
                  <div className="backup-preview-row">
                    <span className="backup-preview-key">Servers</span>
                    <span className="mono">{restorePreview.servers}</span>
                  </div>
                  <div className="backup-preview-row">
                    <span className="backup-preview-key">Tokens</span>
                    <span className="mono">{restorePreview.agentTokens}</span>
                  </div>
                  <div className="backup-preview-row">
                    <span className="backup-preview-key">Settings</span>
                    <span className="mono">{restorePreview.appSettings ? "ya" : "tidak"}</span>
                  </div>
                </div>
              )}
              <button
                className="btn btn-ghost danger"
                type="button"
                disabled={!restorePreview || !!restoreError || restoring}
                style={{
                  opacity: !restorePreview || !!restoreError || restoring ? 0.4 : 1,
                  marginTop: 4,
                }}
                onClick={() => setRestoreConfirmOpen(true)}
              >
                <span>Restore Sekarang</span>
              </button>
              {restoreResult && (
                <div
                  style={{
                    marginTop: 10, padding: "8px 12px", borderRadius: 8,
                    background: "rgba(52,199,89,0.08)",
                    border: "1px solid rgba(52,199,89,0.3)",
                    color: "var(--ok, #34c759)",
                    fontSize: 12,
                  }}
                >
                  ✓ Restore selesai · imported {restoreResult.restored.servers} servers,{" "}
                  {restoreResult.restored.agentTokens} tokens
                  {restoreResult.removed && (restoreResult.removed.servers > 0 || restoreResult.removed.agentTokens > 0) && (
                    <> · removed {restoreResult.removed.servers} servers,{" "}
                    {restoreResult.removed.agentTokens} tokens</>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pembersihan Data */}
        <div className="card-section">
          <h3>🗑 Pembersihan Data</h3>
          <p>
            Hapus log notifikasi & riwayat metrik untuk mengurangi ukuran DB.
            Data server, token agent, dan konfigurasi <b>tidak terpengaruh</b>.
          </p>

          {maintStats && (
            <div className="db-path-label" style={{ marginBottom: 14 }}>
              <span className="db-path-label-key">DB SIZE</span>
              <span className="db-path-label-val mono">
                {formatBytes(maintStats.db.sizeBytes)}
                {maintStats.db.freelistBytes > 0 && (
                  <span style={{ color: "var(--text-3)", marginLeft: 8 }}>
                    ({formatBytes(maintStats.db.freelistBytes)} reclaimable)
                  </span>
                )}
              </span>
            </div>
          )}

          {clearStatus && (
            <div
              style={{
                marginBottom: 10, padding: "8px 12px", borderRadius: 8,
                background: clearStatus.ok ? "rgba(52,199,89,0.08)" : "rgba(255,69,58,0.08)",
                border: `1px solid ${clearStatus.ok ? "rgba(52,199,89,0.3)" : "rgba(255,69,58,0.3)"}`,
                color: clearStatus.ok ? "var(--ok, #34c759)" : "var(--crit)",
                fontSize: 12,
              }}
            >
              {clearStatus.ok ? "✓ " : "✕ "}
              {clearStatus.target === "notifications" && clearStatus.ok &&
                `${clearStatus.deleted} notifikasi dihapus`}
              {clearStatus.target === "metrics" && clearStatus.ok && (
                <>
                  Riwayat metrik dihapus · raw: {clearStatus.deleted.metrics_raw} ·
                  1m: {clearStatus.deleted.metrics_1m} ·
                  1h: {clearStatus.deleted.metrics_1h} ·
                  alert_state: {clearStatus.deleted.alert_state} ·
                  proc: {clearStatus.deleted.processes_latest}
                </>
              )}
              {!clearStatus.ok && clearStatus.msg}
            </div>
          )}

          <div className="backup-grid">
            <div className="backup-card">
              <div className="backup-card-head">
                <span style={{ color: "var(--warn)" }}>🔔</span>
                <span className="backup-card-title">Hapus Log Notifikasi</span>
              </div>
              <div className="backup-card-body">
                Hapus tabel <span className="mono">notifications</span>.
                {maintStats && (
                  <> Saat ini: <b className="mono">{maintStats.notifications.count.toLocaleString()}</b> baris.</>
                )}
              </div>
              <button
                className="btn btn-ghost danger"
                type="button"
                onClick={() => openClearDialog("notifications")}
                disabled={clearing || !maintStats || maintStats.notifications.count === 0}
                style={{ opacity: !maintStats || maintStats.notifications.count === 0 ? 0.4 : 1 }}
              >
                <span>Hapus Notifikasi</span>
              </button>
            </div>

            <div className="backup-card">
              <div className="backup-card-head">
                <span style={{ color: "var(--warn)" }}>📈</span>
                <span className="backup-card-title">Hapus Riwayat Metrik</span>
              </div>
              <div className="backup-card-body">
                Hapus <span className="mono">metrics_raw</span>,{" "}
                <span className="mono">metrics_1m</span>,{" "}
                <span className="mono">metrics_1h</span>, alert state, proses.
                {maintStats && (
                  <>
                    {" "}Total{" "}
                    <b className="mono">
                      {(maintStats.metrics.raw.count + maintStats.metrics["1m"].count + maintStats.metrics["1h"].count).toLocaleString()}
                    </b>
                    {" "}baris.
                  </>
                )}
              </div>
              <button
                className="btn btn-ghost danger"
                type="button"
                onClick={() => openClearDialog("metrics")}
                disabled={clearing || !maintStats || (
                  maintStats.metrics.raw.count === 0 &&
                  maintStats.metrics["1m"].count === 0 &&
                  maintStats.metrics["1h"].count === 0
                )}
                style={{
                  opacity: !maintStats || (
                    maintStats.metrics.raw.count === 0 &&
                    maintStats.metrics["1m"].count === 0 &&
                    maintStats.metrics["1h"].count === 0
                  ) ? 0.4 : 1,
                }}
              >
                <span>Hapus Riwayat Metrik</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation modal for clear operations */}
      {clearTarget && (
        <div className="modal-backdrop" onClick={() => !clearing && setClearTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
            <div className="modal-head">
              <div className="modal-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "var(--crit)" }}><I.trash /></span>
                {clearTarget === "notifications" ? "Hapus Log Notifikasi" : "Hapus Riwayat Metrik"}
              </div>
              <div className="modal-sub">
                {clearTarget === "notifications"
                  ? "Semua log notifikasi (terkirim & gagal) akan dihapus permanen."
                  : "Semua riwayat metrik raw / 1m / 1h + alert state + proses akan dihapus permanen. Grafik dashboard akan kosong sampai agent push tick berikutnya."}
                {" "}<b>Data server, token, dan konfigurasi tidak terpengaruh.</b>
              </div>
            </div>
            <div className="modal-body">
              <div className="field">
                <label className="field-label">
                  Ketik <span className="mono" style={{ color: "var(--text)" }}>HAPUS</span> untuk konfirmasi:
                </label>
                <div className="input">
                  <input
                    value={clearConfirmText}
                    onChange={(e) => setClearConfirmText(e.target.value)}
                    placeholder="HAPUS"
                    style={{ fontFamily: "var(--font-mono)" }}
                    autoFocus
                  />
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => setClearTarget(null)}
                disabled={clearing}
              >
                Batal
              </button>
              <button
                className="btn btn-danger"
                type="button"
                onClick={handleConfirmClear}
                disabled={clearConfirmText !== "HAPUS" || clearing}
                style={{
                  opacity: clearConfirmText !== "HAPUS" || clearing ? 0.5 : 1,
                  pointerEvents: clearConfirmText !== "HAPUS" || clearing ? "none" : "auto",
                }}
              >
                <I.trash />
                <span>{clearing ? "Menghapus…" : "Hapus Permanen"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {restoreConfirmOpen && (
        <div className="modal-backdrop" onClick={() => !restoring && setRestoreConfirmOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
            <div className="modal-head">
              <div className="modal-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "var(--warn)" }}><I.alert /></span>
                Konfirmasi Restore
              </div>
              <div className="modal-sub">
                Operasi ini akan <b>menghapus dan mengganti</b> semua data server, token agent,
                dan konfigurasi yang ada saat ini.
              </div>
            </div>
            <div className="modal-body">
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: "var(--text-2)" }}>
                <li>{restorePreview?.servers ?? 0} server akan di-import (existing dihapus)</li>
                <li>{restorePreview?.agentTokens ?? 0} token akan di-import</li>
                <li>App settings: {restorePreview?.appSettings ? "di-import" : "tidak"}</li>
                <li style={{ color: "var(--text-3)" }}>
                  Riwayat metrik, notifikasi, dan sesi tidak terpengaruh
                </li>
              </ul>
              {restoreError && (
                <div className="backup-error" style={{ marginTop: 12 }}>
                  <I.alert /> <span>{restoreError}</span>
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button
                className="btn btn-ghost"
                type="button"
                disabled={restoring}
                onClick={() => setRestoreConfirmOpen(false)}
              >
                Batal
              </button>
              <button
                className="btn btn-danger"
                type="button"
                disabled={restoring}
                onClick={handleRestoreConfirmed}
                style={{ opacity: restoring ? 0.55 : 1 }}
              >
                <span>{restoring ? "Restoring…" : "Ya, Restore"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ThresholdRow({ icon, iconBg, iconColor, label, value, onChange }) {
  return (
    <div className="thresh-row">
      <div className="thresh-row-label">
        <span className="thresh-row-icon" style={{ background: iconBg, color: iconColor }}>{icon}</span>
        <span>{label}</span>
      </div>
      <div style={{ position: "relative" }}>
        <input
          type="range"
          min="50"
          max="99"
          step="1"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          style={{ width: "100%", margin: 0, accentColor: "var(--accent)" }}
        />
      </div>
      <div className="thresh-input">
        <input
          type="number"
          min="50"
          max="99"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 50)}
        />
        <span>%</span>
      </div>
    </div>
  );
}

function RuleRow({ label, sub, value, onChange, last }) {
  return (
    <div
      className="flex items-center"
      style={{
        padding: "12px 0",
        borderBottom: last ? "none" : "1px solid var(--border-soft)",
        gap: 16,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        <div className="text-xs text-muted" style={{ marginTop: 2 }}>{sub}</div>
      </div>
      <Toggle on={value} onChange={onChange} />
    </div>
  );
}

function NumberRow({ label, sub, value, min, max, step, unit, onChange, last }) {
  // Track local input as a string so the user can clear/retype freely;
  // we coerce + clamp on blur (or Enter) before propagating upward.
  const [raw, setRaw] = useState(String(value));
  useEffect(() => { setRaw(String(value)); }, [value]);

  const commit = () => {
    const n = Number.parseInt(raw, 10);
    if (Number.isNaN(n)) { setRaw(String(value)); return; }
    const clamped = Math.max(min, Math.min(max, n));
    setRaw(String(clamped));
    if (clamped !== value) onChange(clamped);
  };

  return (
    <div
      className="flex items-center"
      style={{
        padding: "12px 0",
        borderBottom: last ? "none" : "1px solid var(--border-soft)",
        gap: 16,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        <div className="text-xs text-muted" style={{ marginTop: 2 }}>{sub}</div>
      </div>
      <div className="flex items-center" style={{ gap: 6 }}>
        <input
          type="number"
          value={raw}
          min={min}
          max={max}
          step={step}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { commit(); e.target.blur(); } }}
          style={{
            width: 80,
            padding: "6px 8px",
            background: "var(--bg-elev-2)",
            border: "1px solid var(--border-soft)",
            borderRadius: 6,
            color: "var(--text)",
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            textAlign: "right",
          }}
        />
        {unit && <span className="text-xs text-muted">{unit}</span>}
      </div>
    </div>
  );
}
